import React from 'react';
import { 
  Zap, 
  Cpu, 
  GitBranch, 
  Users2, 
  CheckCircle2, 
  Code2, 
  Clock, 
  ShieldCheck, 
  Lock, 
  Layers, 
  Award, 
  Sparkles,
  ArrowRight
} from 'lucide-react';

export const WhyChooseUs: React.FC = () => {
  const benefits = [
    {
      title: "Business-Focused Technology Strategy",
      description: "We evaluate every technical choice through commercial value, operational ROI, and user adoption.",
      icon: Zap
    },
    {
      title: "Custom Solutions Tailored to Real Requirements",
      description: "No forced rigid templates; our architectures adapt to your proprietary data schemas and workflows.",
      icon: Cpu
    },
    {
      title: "Modern & Scalable Architecture",
      description: "Cloud-native, microservices, and serverless architectures engineered to handle traffic spikes effortlessly.",
      icon: GitBranch
    },
    {
      title: "User-Centered Design",
      description: "Intuitive, accessible, and high-conversion UX designed to minimize user error and maximize retention.",
      icon: Users2
    },
    {
      title: "Transparent Communication",
      description: "Direct access to lead engineers, regular sprint demos, and real-time project management dashboards.",
      icon: CheckCircle2
    },
    {
      title: "Quality-Focused Development",
      description: "Comprehensive automated unit, integration, and security testing embedded in our CI/CD pipelines.",
      icon: Code2
    },
    {
      title: "Ongoing Technical Support",
      description: "Dedicated maintenance, proactive cloud monitoring, security patches, and SLA response guarantees.",
      icon: Clock
    },
    {
      title: "Continuous Commercial Growth",
      description: "Iterative optimization and keyword-driven marketing to keep generating qualified market demand.",
      icon: Sparkles
    }
  ];

  const engineeringStandards = [
    {
      title: "100% IP Ownership & Clean Code",
      desc: "All source code, design systems, and documentation are transferred directly to your organization with zero proprietary lock-in.",
      icon: Lock,
      color: "text-[#159DE8]",
      bg: "bg-[#159DE8]/10 border-[#159DE8]/30",
    },
    {
      title: "Bi-Weekly Agile Sprints & Demos",
      desc: "Predictable milestone delivery with live staging previews, rapid feedback cycles, and direct communication with lead architects.",
      icon: Layers,
      color: "text-[#FF851B]",
      bg: "bg-[#FF851B]/10 border-[#FF851B]/30",
    },
    {
      title: "Enterprise Quality & Security",
      desc: "End-to-end type safety, automated CI/CD pipelines, regression testing, and security best practices built into every release.",
      icon: ShieldCheck,
      color: "text-[#00C853]",
      bg: "bg-[#00C853]/10 border-[#00C853]/30",
    },
    {
      title: "Proactive Long-Term Website Care",
      desc: "Continuous cloud telemetry, security patching, and performance tuning so your digital systems run reliably 24/7/365.",
      icon: Award,
      color: "text-[#7B2EFF]",
      bg: "bg-[#7B2EFF]/10 border-[#7B2EFF]/30",
    },
  ];

  return (
    <section className="bg-[#050A13] py-20 border-b border-[#1b2f4a] relative overflow-hidden" id="why-choose-us">
      {/* Background ambient lighting */}
      <div className="absolute top-1/3 left-10 w-96 h-96 bg-[#159DE8]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#FF851B]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Left Column: Heading & 8 Core Benefits */}
          <div className="lg:col-span-7 space-y-6">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B] block mb-2">
                THE EBON ADVANTAGE
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-display tracking-tight leading-tight">
                Your Vision. Our Technology. <br className="hidden sm:inline" />
                <span className="text-gradient-orange">Shared Success.</span>
              </h2>
            </div>

            <p className="text-sm sm:text-base text-[#D5DFEC] leading-relaxed">
              We partner with forward-thinking organizations to build mission-critical digital systems that endure, scale, and deliver measurable business outcomes.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
              {benefits.map((b, idx) => {
                const Icon = b.icon;
                return (
                  <div 
                    key={idx} 
                    className="p-3.5 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] flex items-start gap-3 hover:border-[#159DE8]/40 transition-all duration-200 group"
                  >
                    <div className="p-2 rounded-lg bg-[#0B2342] text-[#159DE8] group-hover:text-[#FF851B] group-hover:scale-105 transition-all shrink-0 mt-0.5">
                      <Icon size={16} />
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-white mb-1 group-hover:text-[#FF851B] transition-colors">
                        {b.title}
                      </h3>
                      <p className="text-[11px] text-[#8FA0B8] leading-relaxed">
                        {b.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column: Clean & Modern Engineering Standards Showcase */}
          <div className="lg:col-span-5">
            <div className="p-6 sm:p-7 rounded-2xl bg-[#071426] border border-[#1b2f4a] shadow-2xl relative overflow-hidden space-y-6">
              
              {/* Card Header */}
              <div className="flex items-center justify-between pb-4 border-b border-[#1b2f4a]">
                <div>
                  <span className="text-[11px] font-mono text-[#FF851B] uppercase tracking-wider font-semibold block">
                    HOW WE DELIVER
                  </span>
                  <h3 className="text-base sm:text-lg font-bold text-white font-display">
                    Agency Engineering Standards
                  </h3>
                </div>
                <span className="text-[10px] uppercase font-mono text-[#00C853] bg-green-500/10 px-2.5 py-1 rounded-full border border-green-500/30 font-bold">
                  Verified SLA
                </span>
              </div>

              {/* 4 Clean Pillars */}
              <div className="space-y-3.5">
                {engineeringStandards.map((std, i) => {
                  const Icon = std.icon;
                  return (
                    <div 
                      key={i} 
                      className="p-3.5 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] flex items-start gap-3.5 hover:border-[#159DE8]/40 transition-colors"
                    >
                      <div className={`p-2 rounded-lg ${std.bg} shrink-0 mt-0.5`}>
                        <Icon size={16} className={std.color} />
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-xs font-bold text-white">
                          {std.title}
                        </h4>
                        <p className="text-[11px] text-[#8FA0B8] leading-relaxed">
                          {std.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Performance Key Indicators Bar */}
              <div className="p-4 bg-[#0B2342]/40 rounded-xl border border-[#1b2f4a] grid grid-cols-3 gap-2 text-center">
                <div>
                  <span className="block text-base sm:text-lg font-extrabold text-[#159DE8]">
                    99.98%
                  </span>
                  <span className="block text-[10px] text-[#8FA0B8] leading-tight mt-0.5">
                    Cloud SLA
                  </span>
                </div>
                <div className="border-x border-[#1b2f4a]/80">
                  <span className="block text-base sm:text-lg font-extrabold text-[#FF851B]">
                    &lt; 0.8s
                  </span>
                  <span className="block text-[10px] text-[#8FA0B8] leading-tight mt-0.5">
                    Core Web Vitals
                  </span>
                </div>
                <div>
                  <span className="block text-base sm:text-lg font-extrabold text-[#00C853]">
                    100%
                  </span>
                  <span className="block text-[10px] text-[#8FA0B8] leading-tight mt-0.5">
                    IP Ownership
                  </span>
                </div>
              </div>

              {/* Bottom Trust Assurance Badge */}
              <div className="pt-1 flex items-center justify-between text-xs text-[#8FA0B8]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#00C853] animate-pulse" />
                  <span className="text-[11px]">Direct Engineering Access • No Middlemen</span>
                </div>
                <span className="text-[11px] text-[#159DE8] font-semibold">USA • UK • Canada</span>
              </div>

            </div>
          </div>

        </div>

      </div>
    </section>
  );
};
